import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { X, Loader2, MessageSquareText, Mail, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import { useModalBack } from "@/lib/modalBack";

// Batch 5: one share sheet for token links (sub confirmations, customer
// "What's next"). Send by text opens the phone's own Messages app (sms:),
// Copy link puts it on the clipboard, Send by email uses our mail service.
export default function ShareLinkSheet({ open, onClose, title, message, url, phone = "", email: emailProp = "", onSendEmail }) {
  const { t } = useTranslation();
  const [ph, setPh] = useState(phone);
  const [email, setEmail] = useState(emailProp);
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState(false);
  useModalBack(open, onClose);

  useEffect(() => { if (open) { setPh(phone || ""); setEmail(emailProp || ""); setCopied(false); } }, [open, phone, emailProp]);

  if (!open) return null;

  // S36: clipboard.writeText throws on insecure context / denied permission —
  // fall back to execCommand + show the link so the owner can copy it by hand
  // instead of a bare "Something went wrong".
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t("sl_copied"));
      return;
    } catch { /* fall through to execCommand */ }
    try {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) { setCopied(true); toast.success(t("sl_copied")); return; }
      throw new Error("execCommand false");
    } catch {
      window.prompt(t("sl_copy_manual"), url);
    }
  };

  const sendText = () => {
    const body = encodeURIComponent(`${message}\n${url}`);
    window.location.href = `sms:${ph.trim()}?&body=${body}`;
  };

  const sendEmail = async () => {
    if (busy || !email.trim()) { if (!email.trim()) toast.error(t("cu_missing_contact")); return; }
    setBusy("email");
    try {
      await onSendEmail(email.trim());
      toast.success(t("sl_sent"));
      onClose();
    } catch { toast.error(t("toast_failed")); }
    setBusy("");
  };

  return createPortal(
    <div className="fixed inset-0 z-[96] flex items-end justify-center bg-black/50" onClick={onClose} data-testid="share-link-sheet">
      <div className="w-full max-w-md rounded-t-3xl bg-white p-5 pb-[calc(20px+env(safe-area-inset-bottom,0px))]"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-base font-extrabold text-slate-900">{title}</h3>
          <button onClick={onClose} data-testid="sl-close" aria-label={t("action_cancel")}
            className="flex min-h-[40px] min-w-[40px] items-center justify-center rounded-full text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-3 truncate rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs text-slate-500" data-testid="sl-link">{url}</p>
        <input value={ph} onChange={(e) => setPh(e.target.value)} placeholder={t("sl_phone_ph")} type="text" inputMode="tel"
          data-testid="sl-phone" className="mt-3 h-11 w-full rounded-xl border border-slate-300 px-3 text-sm" />
        <div className="mt-3 flex gap-2">
          <button onClick={sendText} data-testid="sl-send-text"
            className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-full bg-brand text-sm font-bold text-white active:scale-95">
            <MessageSquareText className="h-4 w-4" />{t("sl_send_text")}
          </button>
          <button onClick={copy} data-testid="sl-copy"
            className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-full border border-slate-300 text-sm font-bold text-slate-700 active:scale-95">
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Link2 className="h-4 w-4" />}{t("sl_copy")}
          </button>
        </div>
        {onSendEmail && (
        <div className="mt-3 flex gap-2">
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("sl_email_ph")} type="email"
            data-testid="sl-email" className="h-12 min-w-0 flex-1 rounded-xl border border-slate-300 px-3 text-sm" />
          <button onClick={sendEmail} disabled={!!busy} data-testid="sl-send-email"
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-slate-300 px-4 text-sm font-bold text-slate-700 active:scale-95 disabled:opacity-60">
            {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}{t("sl_send_email")}
          </button>
        </div>
        )}
      </div>
    </div>,
    document.body
  );
}
